import React, { useEffect, useState } from 'react';
import {
  Container,
  Col,
  TabContent,
  TabPane,
  Card,
  CardBody,
} from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import PhoneOutlineIcon from 'mdi-react/PhoneOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import DomainIcon from 'mdi-react/DomainIcon';
import UserForm from './components/UserForm';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import PermissionForm from './components/PermissionForm';
import t, { partial } from '../../util/translation/translation';
import { capitalizeFirstLetter } from '../../util/functions';
import AssignTierForm from './components/AssignTierForm';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import NavTab from '../../shared/components/custom/NavTab';
import FormCard from '../../shared/components/custom/FormCard';

import useConfig from '../../util/useConfig';
import LazyTabPane from '../../shared/components/custom/LazyTabPane';
import UserTicketsPage from './components/UserTicketsPage';

const p = partial('EditUserPage');
const c = partial('CreateUserPage');
const s = partial('shared');
const r = partial('UserRoles');

const EditUserPage = ({ formValues, me }) => {
  const config = useConfig();
  const { userID } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [userAffiliations, setUserAffiliations] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [userServiceId, setUserServiceId] = useState(null);
  const [modalVis, setModalVis] = useState(null);
  const [removeAffiliationId, setRemoveAffiliationId] = useState(null);
  const [teamUserId, setTeamUserId] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tab, setTab] = useState('1');
  const isSuperAdmin = me?.permissions?.includes('super-admin');
  // The Administrator role, shown on the User tab as well
  const adminIndex = permissions.findIndex((perm) => perm.name === 'admin');
  const phone = user?.username
    ? `${user.countryCode ? `+${user.countryCode} ` : ''}${user.username}`
    : '';
  const copyText = (text) =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(p('copied')))
      .catch(() => {});
  const fetchUserCompanies = async (userCloudId) => {
    try {
      const { data: resEmployeeCompanies } =
        await Api.employees.getUserCompaniesForTeam(userCloudId);
      setUserCompanies(resEmployeeCompanies);
    } catch (e) {
      toast.error('Fetching user companies failed!');
    }
  };

  const getUserAffiliationAndTiers = async (userCloudId) => {
    const { data: tierData } = await Api.tiers.allTiers();
    setTiers(tierData);
    try {
      const { data: userAffiliationData } = await Api.tiers.getUserAffiliations(
        userCloudId,
        config
      );
      setUserAffiliations(userAffiliationData);
    } catch (e) {
      toast.error(p('errorFetchingUser'));
    }
  };
  const getUser = async () => {
    setLoading(true);
    try {
      const resUser = await Api.users.getUser(userID, config.cloudIdName);
      const availableRoles = await Api.users.getAvailableRoles();
      await getUserAffiliationAndTiers(resUser.userCloudId);
      await fetchUserCompanies(resUser.userCloudId);
      // Which roles the user has in this club, from the same per-role lists
      // as the Roles tab (users?tab=2). The user's own teams list holds every
      // club they're in, and the first one isn't necessarily this club.
      const holders = await Promise.all(
        availableRoles.map((role) =>
          Api.users.getUsersByRoleName(role.name, config).catch(() => [])
        )
      );
      const asHolder = holders
        .flat()
        .find((holder) => holder?.userCloudId === resUser.userCloudId);
      const mappedRoles = availableRoles.map((perm, index) => ({
        ...perm,
        toggled: (holders[index] || []).some(
          (holder) => holder?.userCloudId === resUser.userCloudId
        ),
      }));
      setPermissions(mappedRoles);
      setUserServiceId(resUser.id);
      // The team user in this club, as the Roles tab uses for changes
      setTeamUserId(asHolder?.teamUserId || resUser.teams[0].id);
      setUser({ ...resUser, companies: [] });
    } catch (err) {
      toast.error(err?.message ?? p('errorFetchingUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (e) => {
    // Copy the policy too: a failed save must leave the shown state as it was
    const _permissions = [...permissions];
    _permissions[e] = { ..._permissions[e], toggled: !_permissions[e].toggled };
    setLoading(true);
    try {
      const { toggled, name } = _permissions[e];
      await Api.users.updateUserRoles(teamUserId, name, toggled);
      setPermissions(_permissions);
    } catch (err) {
      toast.error(err || p('errorGivingUserPolicies'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      firstname: formValues.firstName,
      lastname: formValues.lastName,
      zip: formValues.postalCode,
      dateOfBirth: formValues.birthday
        ? moment(formValues.birthday).format('YYYY-MM-DD')
        : null,
      gender: formValues.gender,
      email: formValues.email,
      phoneNumber: `+${formValues.countryCode}${formValues.username}`,
    };
    setLoading(true);
    try {
      const result = await Api.users.updateUserGraphQL(userServiceId, payload);
      toast.success(p('userWasUpdated'));
      history.replace('/users');
    } catch (err) {
      const isDuplicatePhone =
        err?.response?.errors[0]?.message?.includes('Unique');
      if (isDuplicatePhone) {
        toast.error(c('phoneExists'));
      } else {
        toast.error(s('somethingWentWrong'));
      }
    } finally {
      setLoading(false);
    }
  };
  const handleSubmitTier = async (values) => {
    const {
      dueDate,
      neverEnd,
      sendPushNotification,
      tier: { value: pickedTier },
    } = values;
    const { username, countryCode, userCloudId } = user;
    const payload = {
      dueDate,
      neverEnd,
      tierId: pickedTier === 'empty' ? null : pickedTier,
      phone: username,
      countryCode,
      cloudId: userCloudId,
      sendPushNotification: sendPushNotification,
    };
    setLoading(true);
    Api.tiers
      .assignUserToTier(payload, config)
      .then(() => {
        toast.success(p('userWasUpdated'));
        history.replace('/users');
      })
      .catch((err) => {
        toast.error(err || s('somethingWentWrong'));
      })
      .finally(() => setLoading(false));
  };

  const deleteAffiliation = async () => {
    setLoading(true);
    try {
      const { userCloudId } = user;
      await Api.tiers.deleteUserAffiliation(removeAffiliationId, config);
      const { data: userAffiliationData } = await Api.tiers.getUserAffiliations(
        userCloudId,
        config
      );
      setUserAffiliations(userAffiliationData);
      setModalVis(false);
      setRemoveAffiliationId(null);
    } catch (e) {
      toast.error(e || p('failedToDeleteAffiliation'));
    } finally {
      setLoading(false);
    }
  };

  const handleModal = (isVis, affId) => {
    setModalVis(isVis);
    setRemoveAffiliationId(affId);
  };

  useEffect(() => {
    if (userID && config) {
      getUser();
    }
  }, [userID, config]);

  return (
    <Container>
      <DeleteModal
        type={t('UserTiers.affiliation')}
        visible={modalVis}
        handleModal={handleModal}
        modalAction={deleteAffiliation}
      />
      <Loading loading={loading} />
      {/* Who this is, before the tabs: name, contact and companies */}
      <div className="user-head mb-3">
        <span className="user-head__avatar" aria-hidden>
          {[user?.firstname, user?.lastname]
            .filter(Boolean)
            .map((name) => name[0].toUpperCase())
            .join('') || '?'}
        </span>
        <div className="user-head__text">
          <h3 className="user-head__name">
            {[user?.firstname, user?.lastname].filter(Boolean).join(' ') ||
              p('editUser')}
          </h3>
          {/* Phone and email, each a link with a copy button */}
          <div className="user-head__contact">
            {phone && (
              <span className="user-head__contact-item">
                <PhoneOutlineIcon size={16} aria-hidden />
                <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
                <button
                  type="button"
                  className="user-head__copy"
                  title={capitalizeFirstLetter(s('copy'))}
                  aria-label={`${capitalizeFirstLetter(s('copy'))} ${s('phone')}`}
                  onClick={() => copyText(phone)}
                >
                  <ContentCopyIcon size={14} />
                </button>
              </span>
            )}
            {user?.email && (
              <span className="user-head__contact-item">
                <EmailOutlineIcon size={16} aria-hidden />
                <a href={`mailto:${user.email}`}>{user.email}</a>
                <button
                  type="button"
                  className="user-head__copy"
                  title={capitalizeFirstLetter(s('copy'))}
                  aria-label={`${capitalizeFirstLetter(s('copy'))} ${s('email')}`}
                  onClick={() => copyText(user.email)}
                >
                  <ContentCopyIcon size={14} />
                </button>
              </span>
            )}
          </div>
          {userCompanies?.length > 0 && (
            <div className="user-head__companies">
              <span className="user-head__label">{s('companies')}</span>
              {userCompanies.map((company) => {
                const logo = company?.images?.companyImage;
                return (
                  <Link
                    key={company.id}
                    to={`/companies/edit/${company.id}?tab=1`}
                    className="user-head__company"
                  >
                    <span className="user-head__company-logo" aria-hidden>
                      {logo && logo !== 'NULL' ? (
                        <img src={logo} alt="" />
                      ) : (
                        <DomainIcon size={14} />
                      )}
                    </span>
                    {company.name}
                    <ChevronRightIcon size={14} aria-hidden />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Card>
        <CardBody className="p-0">
          <NavTab
            tabs={[
              { tab: `${s('user')}` },
              { tab: `${s('policies')}` },
              { tab: `${capitalizeFirstLetter(t('UserTiers.tier'))}` },
              isSuperAdmin && { tab: s('ticket') },
            ].filter(Boolean)}
            setTab={setTab}
            activeTab={tab}
            size="sm"
          />
          <Col className="pt-3 pb-3">
            <TabContent activeTab={tab}>
              <TabPane tabId="1">
                <UserForm
                  type="edit"
                  onSubmit={handleSubmit}
                  user={user}
                  userCompanies={userCompanies}
                >
                  {/* The most used policy, here without a trip to Policies */}
                  {adminIndex !== -1 && (
                    <FormCard
                      title={p('access')}
                      actions={
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mb-0"
                          onClick={() => setTab('2')}
                        >
                          {p('allPolicies')}
                        </button>
                      }
                    >
                      <div className="option-list">
                        <div className="switch-row">
                          <label
                            className="switch-row__text"
                            htmlFor="user-admin-role"
                          >
                            <span className="switch-row__label">
                              {r('admin')}
                            </span>
                            <span className="switch-row__description">
                              {r('adminDescription')}
                            </span>
                          </label>
                          <div className="switch-btn">
                            <label
                              className={`switch-btn--container${
                                permissions[adminIndex].toggled ? ' active' : ''
                              }`}
                            >
                              <input
                                id="user-admin-role"
                                className="switch-btn--input"
                                type="checkbox"
                                checked={!!permissions[adminIndex].toggled}
                                disabled={loading}
                                onChange={() => handleToggle(adminIndex)}
                              />
                              <span className="switch-btn--button" />
                            </label>
                          </div>
                        </div>
                      </div>
                    </FormCard>
                  )}
                </UserForm>
              </TabPane>
              <TabPane tabId="2">
                <PermissionForm
                  permissions={permissions}
                  handleToggle={handleToggle}
                  loading={loading}
                />
              </TabPane>
              <TabPane tabId="3">
                <AssignTierForm
                  availableTiers={tiers}
                  currentUserAffiliatons={userAffiliations}
                  onSubmit={handleSubmitTier}
                  handleModal={handleModal}
                />
              </TabPane>
              <LazyTabPane activeTab={tab} tabId="4">
                <UserTicketsPage
                  userID={userID}
                  phoneNumber={user?.phoneNumber}
                />
              </LazyTabPane>
            </TabContent>
          </Col>
        </CardBody>
      </Card>
    </Container>
  );
};

export default connect((state) => ({
  formValues: getFormValues('UserForm')(state),
  me: state.admin.me,
}))(EditUserPage);
